import { Ref, useImperativeHandle, useRef } from 'react';
import { useDebounce, useMount } from 'react-use';

import { SentPrescriptionPackage } from '~libs.shared.prescription';

import { DraftPrescriptionsFormController } from './DraftPrescriptionsForm.types';
import { useVisitPrescriptionListDraftUpdateCommand } from './DraftPrescriptionsRouteLeavingGuard.hooks';

const PRESCRIPTIONS_LIST_UPDATE_DEBOUNCE_TIME_IN_MS = 500;

export type DraftPrescriptionsListUpdateApi = {
   disable: VoidFunction;
};

type DraftPrescriptionsListUpdateProps = Pick<DraftPrescriptionsFormController, 'handleSubmitDraft' | 'watch'> & {
   readonly sentPrescriptionPackages: SentPrescriptionPackage[];
   readonly visitId: number;
   readonly draftPrescriptionsListUpdateApiRef?: Ref<DraftPrescriptionsListUpdateApi>;
};

export const DraftPrescriptionsListUpdate = (props: DraftPrescriptionsListUpdateProps): null => {
   const draftPrescriptions = props.watch('draftPrescriptions');
   const draftPrescriptionsKey = JSON.stringify(
      draftPrescriptions.map((draft) => (draft.type === 'standard' ? draft.data.extendedDosage : null))
   );
   const { executeAsync: updateDraftPrescriptionsList } = useVisitPrescriptionListDraftUpdateCommand();
   const disabledRef = useRef(false);

   const [, cancel] = useDebounce(
      () => {
         if (disabledRef.current) {
            return;
         }

         props.handleSubmitDraft(async ({ draftPrescriptions, prescriptionCategory }) => {
            return updateDraftPrescriptionsList({
               visitId: props.visitId,
               sentPrescriptionPackages: props.sentPrescriptionPackages,
               draftPrescriptions: draftPrescriptions,
               prescriptionCategory: prescriptionCategory,
            });
         })();
      },
      PRESCRIPTIONS_LIST_UPDATE_DEBOUNCE_TIME_IN_MS,
      [draftPrescriptions.length, draftPrescriptionsKey]
   );

   // Cancel initial draft save action
   useMount(cancel);

   useImperativeHandle(props.draftPrescriptionsListUpdateApiRef, () => ({
      disable: () => {
         disabledRef.current = true;
         cancel();
      },
   }));

   return null;
};
